// Product settings > FAQs: the questions the product's pages answer. Page
// rows come from the page checks (FAQPage markup on every check, the
// classifier when a copy change added an FAQ) and leave when two
// consecutive checks of their page no longer show them; manual rows are the
// brand's own and never move. Each row can hold the experts' answers
// (desires_faqs_answers), stored for later use.
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import classNames from 'classnames';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import axiosInstant from '../../../../api';
import { productFaqAnswersEndpoint, productFaqEndpoint, productFaqsEndpoint, productFaqsScanEndpoint } from '../../../../api/endpoints';
import { useUser } from '../../../../hooks/useUser';
import { isInternal } from '../../../../utils';
import { apiErrorCode } from '../../../../utils/apiError';
import { IconChevronDown } from '../../../../utils/icons';
import { ProductFaq, ProductFaqsResponse } from '../../../../types/productFaqs';
import { Pill, btnGhost, btnPrimary, btnSecondary } from './autopilotLabels';

interface Props {
  desireid: number;
}

const inputClass =
  'w-full px-3 py-2 rounded-lg border border-border-token bg-surface text-[13px] text-text-1 focus:outline-none focus:border-accent-text';

const SOURCE_LABELS: Record<string, string> = {
  page_jsonld: 'From the page markup',
  page_text: 'From the page text',
  manual: 'Added here',
};

const hostOf = (url?: string | null): string | null => {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

function FaqRow({
  faq,
  desireid,
  onChanged,
}: {
  faq: ProductFaq;
  desireid: number;
  onChanged: () => void;
}) {
  const { role } = useUser();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [question, setQuestion] = useState(faq.question);
  const [answer, setAnswer] = useState(faq.answer || '');
  const [busy, setBusy] = useState(false);
  const [newAnswer, setNewAnswer] = useState('');

  useEffect(() => {
    setQuestion(faq.question);
    setAnswer(faq.answer || '');
  }, [faq.question, faq.answer]);

  const save = async () => {
    setBusy(true);
    try {
      await axiosInstant.patch(productFaqEndpoint(desireid, faq.id), { question, answer: answer || null });
      toast.success('FAQ saved.');
      setEditing(false);
      onChanged();
    } catch (e: any) {
      toast.error(apiErrorCode(e) === 'QuestionTooShort' ? 'The question is too short.' : "Couldn't save the FAQ. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm('Remove this FAQ from the product? If it is still on the page, the next page check brings it back.')) return;
    setBusy(true);
    try {
      await axiosInstant.delete(productFaqEndpoint(desireid, faq.id));
      onChanged();
    } catch {
      toast.error("Couldn't remove the FAQ. Please try again.");
      setBusy(false);
    }
  };
  const addAnswer = async () => {
    if (!newAnswer.trim()) return;
    setBusy(true);
    try {
      await axiosInstant.post(productFaqAnswersEndpoint(desireid, faq.id), { answer: newAnswer.trim() });
      setNewAnswer('');
      onChanged();
    } catch {
      toast.error("Couldn't save the answer. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  const removeAnswer = async (id: number) => {
    setBusy(true);
    try {
      await axiosInstant.delete(`${productFaqAnswersEndpoint(desireid, faq.id)}/${id}`);
      onChanged();
    } catch {
      toast.error("Couldn't remove the answer. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const host = hostOf(faq.pdp_url && faq.pdp_url.pdp_url);
  return (
    <Box className="rounded-[12px] border border-border-token bg-surface px-3 py-2">
      <Box className="flex items-start justify-between gap-2">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex-1 min-w-0 text-left bg-transparent border-0 p-0 cursor-pointer" aria-expanded={open}>
          <Box className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-text-1">{faq.question}</span>
            <Pill tone={faq.source === 'manual' ? 'accent' : 'neutral'} title={faq.source === 'manual' ? 'Added here; the page checks never change it' : 'Read from the product page; it leaves when two page checks no longer show it'}>
              {SOURCE_LABELS[faq.source] || faq.source}
            </Pill>
            {faq.missing_count > 0 ? (
              <Pill tone="warn" title="The last page check did not show this question; one more and it is removed">Not seen on the last check</Pill>
            ) : null}
            {faq.answers.length ? <Pill tone="good">{faq.answers.length === 1 ? '1 expert answer' : `${faq.answers.length} expert answers`}</Pill> : null}
          </Box>
          <Box className="flex items-center gap-3 flex-wrap text-[11px] text-muted mt-1">
            {host ? <span>{host}</span> : null}
            {faq.last_seen_at ? <span>Seen {dayjs(faq.last_seen_at).format('D MMM YYYY')}</span> : null}
          </Box>
          {!open && faq.answer ? <p className="text-[12.5px] text-text-2 m-0 mt-1 line-clamp-2">{faq.answer}</p> : null}
        </button>
        <button type="button" onClick={() => setOpen((v) => !v)} className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0" aria-label={open ? 'Collapse' : 'Expand'}>
          <IconChevronDown size={14} open={open} />
        </button>
      </Box>
      {open ? (
        <Box className="flex flex-col gap-3 pt-3 mt-2 border-t border-border-token">
          {editing ? (
            <Box className="flex flex-col gap-2">
              <input className={inputClass} value={question} onChange={(e) => setQuestion(e.target.value)} disabled={busy} aria-label="Question" />
              <textarea className={classNames(inputClass, 'min-h-[80px]')} value={answer} onChange={(e) => setAnswer(e.target.value)} disabled={busy} aria-label="Answer on the page" placeholder="The answer as the page gives it" />
              <Box className="flex items-center gap-2">
                <button type="button" className={btnPrimary} onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
                <button type="button" className={btnGhost} onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
              </Box>
              {faq.source !== 'manual' ? <span className="text-[11px] text-muted">Editing makes this the brand&apos;s own FAQ; the page checks stop managing it.</span> : null}
            </Box>
          ) : (
            <Box>
              <span className="text-[11px] font-bold text-muted uppercase tracking-[0.4px]">Answer on the page</span>
              <p className="text-[12.5px] text-text-2 m-0 mt-1 whitespace-pre-wrap">{faq.answer || <span className="text-muted">No answer captured.</span>}</p>
              <Box className="flex items-center gap-2 mt-2">
                <button type="button" className={btnSecondary} onClick={() => setEditing(true)} disabled={busy}>Edit</button>
                <button type="button" className={btnGhost} onClick={remove} disabled={busy}>Remove</button>
              </Box>
            </Box>
          )}
          <Box>
            <span className="text-[11px] font-bold text-muted uppercase tracking-[0.4px]">Expert answers</span>
            {faq.answers.length ? (
              <ul className="m-0 mt-1 pl-0 list-none flex flex-col gap-2">
                {faq.answers.map((a) => (
                  <li key={a.id} className="rounded-[10px] bg-surface-2 px-3 py-2 text-[12.5px] text-text-2">
                    <Box className="flex items-center justify-between gap-2">
                      <span className="text-[11px] text-muted">
                        {a.status === 'approved' ? 'Approved' : 'Draft'}
                        {a.pro_userid ? ` · expert #${a.pro_userid}` : ''} · {dayjs(a.created_at).format('D MMM YYYY')}
                      </span>
                      {isInternal(role) ? (
                        <button type="button" className={btnGhost} onClick={() => removeAnswer(a.id)} disabled={busy}>Remove</button>
                      ) : null}
                    </Box>
                    <p className="m-0 mt-1 whitespace-pre-wrap">{a.answer}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[12px] text-muted m-0 mt-1">No expert answers yet.</p>
            )}
            {isInternal(role) ? (
              <Box className="flex flex-col gap-2 mt-2">
                <textarea className={classNames(inputClass, 'min-h-[60px]')} value={newAnswer} onChange={(e) => setNewAnswer(e.target.value)} disabled={busy} placeholder="An expert's answer, for later use" aria-label="Expert answer" />
                <Box>
                  <button type="button" className={btnSecondary} onClick={addAnswer} disabled={busy || !newAnswer.trim()}>Add answer</button>
                </Box>
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

export function ProductFaqsPanel({ desireid }: Props) {
  const { role } = useUser();
  const [faqs, setFaqs] = useState<ProductFaq[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    try {
      const { data } = await axiosInstant.get<ProductFaqsResponse>(productFaqsEndpoint(desireid));
      setFaqs(data.faqs || []);
      setError(null);
    } catch {
      setError("Couldn't load the FAQs.");
    }
  };
  useEffect(() => {
    load();
  }, [desireid]);

  const add = async () => {
    setBusy(true);
    try {
      await axiosInstant.post(productFaqsEndpoint(desireid), { question: question.trim(), answer: answer.trim() || null });
      setQuestion('');
      setAnswer('');
      setAdding(false);
      await load();
    } catch (e: any) {
      const code = apiErrorCode(e);
      if (code === 'FaqAlreadyExists') toast.error('That question is already on the product.');
      else if (code === 'QuestionTooShort') toast.error('The question is too short.');
      else toast.error("Couldn't add the FAQ. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await axiosInstant.post<ProductFaqsResponse & { pages: Array<{ page_id: number; skipped?: string; created?: number; removed?: number }> }>(productFaqsScanEndpoint(desireid));
      setFaqs(data.faqs || []);
      const created = (data.pages || []).reduce((n, p) => n + (p.created || 0), 0);
      const skipped = (data.pages || []).filter((p) => p.skipped).length;
      toast.success(`${created} new FAQ${created === 1 ? '' : 's'} found${skipped ? ` · ${skipped} page${skipped === 1 ? '' : 's'} without a page check yet` : ''}.`);
    } catch {
      toast.error("Couldn't scan the pages. Please try again.");
    } finally {
      setScanning(false);
    }
  };

  const pageRows = (faqs || []).filter((f) => f.source !== 'manual');
  const manualRows = (faqs || []).filter((f) => f.source === 'manual');

  return (
    <div className="flex flex-col gap-4 pt-4">
      <Box className="flex items-center justify-between gap-2 flex-wrap">
        <Box>
          <h4 className="text-[14px] font-semibold text-text-1 m-0">FAQs</h4>
          <p className="text-[12.5px] text-text-2 m-0 mt-1">
            The questions this product&apos;s pages answer. Page checks keep the list in step with the page: a question that disappears from the page leaves here too.
          </p>
        </Box>
        <Box className="flex items-center gap-2">
          {isInternal(role) ? (
            <button type="button" className={btnSecondary} onClick={scan} disabled={scanning} title="Re-read every page's latest page check for FAQs (one AI call per page)">
              {scanning ? 'Scanning…' : 'Scan pages'}
            </button>
          ) : null}
          <button type="button" className={btnPrimary} onClick={() => setAdding((v) => !v)}>
            Add FAQ
          </button>
        </Box>
      </Box>

      {adding ? (
        <Box className="rounded-[12px] border border-dashed border-border-token bg-surface-2 p-3 flex flex-col gap-2">
          <input className={inputClass} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Question" disabled={busy} aria-label="Question" />
          <textarea className={classNames(inputClass, 'min-h-[70px]')} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Answer (optional)" disabled={busy} aria-label="Answer" />
          <Box className="flex items-center gap-2">
            <button type="button" className={btnPrimary} onClick={add} disabled={busy || question.trim().length < 6}>{busy ? 'Adding…' : 'Add'}</button>
            <button type="button" className={btnGhost} onClick={() => setAdding(false)} disabled={busy}>Cancel</button>
          </Box>
        </Box>
      ) : null}

      {error ? <p className="text-[13px] text-down m-0">{error}</p> : null}
      {faqs === null && !error ? <p className="text-[12.5px] text-muted m-0">Loading…</p> : null}

      {faqs && !faqs.length ? (
        <Box className="rounded-[12px] border border-dashed border-border-token bg-surface-2 p-4 text-center">
          <p className="text-[12.5px] text-muted m-0">No FAQs yet. They appear when a page check finds an FAQ on a product page, or add one here.</p>
        </Box>
      ) : null}

      {pageRows.length ? (
        <Box className="flex flex-col gap-2">
          <span className="text-[11px] font-bold text-muted uppercase tracking-[0.4px]">On the page</span>
          {pageRows.map((f) => (
            <FaqRow key={f.id} faq={f} desireid={desireid} onChanged={load} />
          ))}
        </Box>
      ) : null}
      {manualRows.length ? (
        <Box className="flex flex-col gap-2">
          <span className="text-[11px] font-bold text-muted uppercase tracking-[0.4px]">Added here</span>
          {manualRows.map((f) => (
            <FaqRow key={f.id} faq={f} desireid={desireid} onChanged={load} />
          ))}
        </Box>
      ) : null}
    </div>
  );
}
